import { useCallback, useEffect, useRef, useState } from 'react'
import type { TangerLayerSpec } from '../map/tileUrl'

export type BaseMapPhase = 'loading' | 'ready' | 'error'

type Call = <T>(path: string) => Promise<T>

// The call is injected rather than resolved from context so the hook can be
// driven directly in a test without mounting an auth provider.
export function useBaseMap(call: Call) {
  const [phase, setPhase] = useState<BaseMapPhase>('loading')
  const [spec, setSpec] = useState<TangerLayerSpec | null>(null)
  const [attempt, setAttempt] = useState(0)

  // `call` is not a dependency of the effect below. useApiFetch's returned
  // function is a useCallback keyed on live auth state, so its identity can
  // change between renders; if the effect re-ran on every such change it
  // would restart the in-flight request each time phase settles and
  // re-renders the component, which recomputes `call` again — a
  // loading/settled loop that never lets the phase stick. Keying the effect
  // on `attempt` alone, and always reading the latest `call` through this
  // ref, decouples "when to fetch" (mount + explicit retry) from "which
  // function instance to call" (always the newest one).
  const callRef = useRef(call)
  callRef.current = call

  useEffect(() => {
    let cancelled = false
    setPhase('loading')
    callRef.current<TangerLayerSpec>('/basemap')
      .then((res) => {
        if (!cancelled) {
          setSpec(res)
          setPhase('ready')
        }
      })
      .catch(() => {
        if (!cancelled) setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  return { phase, spec, retry }
}
